import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  User,
  Phone,
  Briefcase,
  Clock,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import MainHeading from "../../components/common/MainHeading";
import { Button } from "../../components/ui/button";
import { showToastNotification } from "../../utility/helper";
import { RoutesLink } from "../../utility/RouteLinks";
import type {
  IWorkerCreatePayload,
} from "@/interface/worker.model";
import { WorkerService } from "@/services/Labor/worker";
import { SiteService } from "@/services/Site/Site";
import { ShiftService } from "@/services/Shift/Shift";
import AsyncSearchSelect, {
  type IOption,
} from "@/components/common/AsyncSearchSelect";
import { loadShiftOptions, loadSiteOptions } from "@/hooks/Selectloaders";

// Form interface 
interface ILaborForm {
  workerId: string;
  fullName: string;
  fatherName: string;
  gender: "male" | "female" | "other" | "";
  phoneNumber: string;
  aadharNumber: string;
  siteId: string;
  shiftId: string;
  status: "active" | "inactive";
  joinDate: string;
  dailyRate: number | string;
  contactPerson: string;
  cpNumber: string;
  checkInBufferTime: string;
  checkOutBufferTime: string;
}

const INITIAL_FORM: ILaborForm = {
  workerId: "",
  fullName: "",
  fatherName: "",
  gender: "",
  phoneNumber: "",
  aadharNumber: "",
  siteId: "",
  shiftId: "",
  status: "active",
  joinDate: "",
  dailyRate: "",
  contactPerson: "",
  cpNumber: "",
  checkInBufferTime: "",
  checkOutBufferTime: "",
};

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const inputClass =
  "w-full bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all";

const labelClass = "text-[13px] font-medium text-gray-700 mb-1.5 block";

const toTimeInputValue = (value?: string | null) => {
  if (!value) return "";
  return value.slice(0, 5);
};


const FormSection = ({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="border-b border-gray-100 last:border-b-0 px-6 py-7 sm:px-8">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-9 h-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-[13px] text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);


const CreateLabor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<ILaborForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ILaborForm, string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);

  const [selectedSite, setSelectedSite] = useState<IOption | null>(null);
  const [selectedShift, setSelectedShift] = useState<IOption | null>(null);

  const [enableTimings, setEnableTimings] = useState(false);

  // ─── Prefill on edit ──────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;

    const fetchDetail = async () => {
      setIsLoading(true);
      try {
        const workerRes = await WorkerService.getWorkerDetail(id);

        if (!workerRes?.success || !workerRes.data) {
          showToastNotification("danger", "Failed to load worker");
          navigate(RoutesLink.labor);
          return;
        }

        const worker = workerRes.data;

        setForm({
          workerId: worker.workerId ?? "",
          fullName: worker.fullName ?? "",
          fatherName: worker.fatherName ?? "",
          gender: (worker.gender as ILaborForm["gender"]) ?? "",
          phoneNumber: worker.phoneNumber ?? "",
          aadharNumber: worker.aadharNumber ?? "",
          siteId: worker.siteId ?? "",
          shiftId: worker.shiftId ?? "",
          status: worker.status ?? "active",
          joinDate: (worker.joinDate ?? "").slice(0, 10),
          dailyRate: worker.dailyRate ?? "",
          contactPerson: worker.contactPerson ?? "",
          cpNumber: worker.cpNumber ?? "",
          checkInBufferTime: toTimeInputValue(worker.checkInBufferTime),
          checkOutBufferTime: toTimeInputValue(worker.checkOutBufferTime),
        });

  
        const [siteRes, shiftRes] = await Promise.all([
          worker.siteId ? SiteService.getSiteDetail(worker.siteId) : null,
          worker.shiftId ? ShiftService.getShiftDetail(worker.shiftId) : null,
        ]);

        if (siteRes?.success && siteRes.data) {
          setSelectedSite({
            value: siteRes.data.id,
            label: siteRes.data.siteName,
          });
        }
        if (shiftRes?.success && shiftRes.data) {
          setSelectedShift({
            value: shiftRes.data.id,
            label: shiftRes.data.shiftName,
          });
        }

        const hasBuffer =
          (worker.checkInBufferTime &&
            worker.checkInBufferTime !== "00:00:00") ||
          (worker.checkOutBufferTime &&
            worker.checkOutBufferTime !== "00:00:00");
        setEnableTimings(Boolean(hasBuffer));
      } catch {
        showToastNotification("danger", "Failed to load worker");
        navigate(RoutesLink.labor);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  const handleChange = (
    field: keyof ILaborForm,
    value: string | number | boolean,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const setCurrentTime = (
    field: "checkInBufferTime" | "checkOutBufferTime",
  ) => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    handleChange(field, `${hours}:${minutes}`);
  };

  const handleSiteChange = (option: IOption | null) => {
    setSelectedSite(option);
    handleChange("siteId", option?.value ?? "");
  };

  const handleShiftChange = (option: IOption | null) => {
    setSelectedShift(option);
    handleChange("shiftId", option?.value ?? "");
  };

  const validate = (): boolean => {
    const nextErrors: Partial<Record<keyof ILaborForm, string>> = {};

    if (!form.workerId.trim()) nextErrors.workerId = "Worker ID is required";
    if (!form.fullName.trim()) nextErrors.fullName = "Full name is required";
    if (!form.gender) nextErrors.gender = "Gender is required";
    if (!form.phoneNumber.trim()) {
      nextErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(form.phoneNumber.replace(/\s+/g, ""))) {
      nextErrors.phoneNumber = "Enter a valid 10-digit number";
    }
    if (!form.siteId) nextErrors.siteId = "Site is required";
    if (!form.shiftId) nextErrors.shiftId = "Shift is required";
    if (!form.joinDate) nextErrors.joinDate = "Joining date is required";
    if (form.dailyRate && Number(form.dailyRate) < 0) {
      nextErrors.dailyRate = "Daily rate cannot be negative";
    }
    if (form.cpNumber && !/^\d{10}$/.test(form.cpNumber.replace(/\s+/g, ""))) {
      nextErrors.cpNumber = "Enter a valid 10-digit number";
    }
    if (enableTimings) {
      if (!form.checkInBufferTime)
        nextErrors.checkInBufferTime = "Check-in time is required";
      if (!form.checkOutBufferTime)
        nextErrors.checkOutBufferTime = "Check-out time is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const basePayload: IWorkerCreatePayload = {
        workerId: form.workerId,
        fullName: form.fullName,
        fatherName: form.fatherName || undefined,
        phoneNumber: form.phoneNumber,
        aadharNumber: form.aadharNumber || undefined,
        siteId: form.siteId,
        shiftId: form.shiftId,
        checkInBufferTime: enableTimings
          ? `${form.checkInBufferTime}:00`
          : "00:00:00",
        checkOutBufferTime: enableTimings
          ? `${form.checkOutBufferTime}:00`
          : "00:00:00",
        dailyRate: Number(form.dailyRate) || 0,
        joinDate: form.joinDate,
        status: form.status,
        contactPerson: form.contactPerson,
        cpNumber: form.cpNumber,
        gender: form.gender as "male" | "female" | "other",
      };

      if (isEditMode && id) {
        // 🛑 Omit workerId for update – it's not allowed by the API
        const { workerId, ...updatePayload } = basePayload;
        const response = await WorkerService.updateWorker(id, updatePayload);
        if (response.success) {
          showToastNotification("success", "Worker updated successfully");
          navigate(RoutesLink.labor);
        } else {
          showToastNotification(
            "danger",
            response.message || "Failed to update worker",
          );
        }
      } else {
        const response = await WorkerService.createWorker(basePayload);
        if (response.success) {
          showToastNotification("success", "Worker added successfully");
          navigate(RoutesLink.labor);
        } else {
          showToastNotification(
            "danger",
            response.message || "Failed to add worker",
          );
        }
      }
    } catch {
      showToastNotification(
        "danger",
        isEditMode ? "Failed to update worker" : "Failed to add worker",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
        <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
          Loading worker...
        </div>
    );
  }


  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          className="h-9 w-9 p-0 hover:bg-muted shrink-0"
          onClick={() => navigate(RoutesLink.labor)}
          type="button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <MainHeading title={isEditMode ? "Edit Worker" : "Add Worker"} />
          <p className="text-[13px] text-gray-400 -mt-0.5">
            {isEditMode
              ? "Update worker details and site assignment"
              : "Register a new worker and their site assignment"}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden"
      >
        <FormSection
          icon={<User size={17} />}
          title="Basic details"
          description="Identity and contact information"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5">
            <div>
              <label className={labelClass}>
                Worker ID <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="Enter worker code"
                value={form.workerId}
                onChange={(e) => handleChange("workerId", e.target.value)}
                disabled={isEditMode} // <-- Worker ID cannot be changed in edit mode
              />
              {errors.workerId && (
                <p className="text-xs text-red-500 mt-1.5">{errors.workerId}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="Enter full name"
                value={form.fullName}
                onChange={(e) => handleChange("fullName", e.target.value)}
              />
              {errors.fullName && (
                <p className="text-xs text-red-500 mt-1.5">{errors.fullName}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Father's name</label>
              <input
                className={inputClass}
                placeholder="Enter father's name"
                value={form.fatherName}
                onChange={(e) => handleChange("fatherName", e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                className={inputClass}
                value={form.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
              >
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
              {errors.gender && (
                <p className="text-xs text-red-500 mt-1.5">{errors.gender}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Phone number <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                placeholder="10-digit mobile number"
                value={form.phoneNumber}
                onChange={(e) => handleChange("phoneNumber", e.target.value)}
              />
              {errors.phoneNumber && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.phoneNumber}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>Aadhar number</label>
              <input
                className={inputClass}
                placeholder="12-digit Aadhar number"
                value={form.aadharNumber}
                onChange={(e) => handleChange("aadharNumber", e.target.value)}
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          icon={<Briefcase size={17} />}
          title="Work details"
          description="Site, shift, and compensation"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5">
            <div>
              <label className={labelClass}>
                Site <span className="text-red-500">*</span>
              </label>
              <AsyncSearchSelect
                value={selectedSite}
                onChange={handleSiteChange}
                loadOptions={loadSiteOptions}
                placeholder="Search site…"
                hasError={!!errors.siteId}
              />
              {errors.siteId && (
                <p className="text-xs text-red-500 mt-1.5">{errors.siteId}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Shift <span className="text-red-500">*</span>
              </label>
              <AsyncSearchSelect
                value={selectedShift}
                onChange={handleShiftChange}
                loadOptions={loadShiftOptions}
                placeholder="Search shift…"
                hasError={!!errors.shiftId}
              />
              {errors.shiftId && (
                <p className="text-xs text-red-500 mt-1.5">{errors.shiftId}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Daily rate (₹)</label>
              <input
                className={inputClass}
                type="number"
                min={0}
                step="0.01"
                placeholder="e.g. 650"
                value={form.dailyRate}
                onChange={(e) => handleChange("dailyRate", e.target.value)}
              />
              {errors.dailyRate && (
                <p className="text-xs text-red-500 mt-1.5">
                  {errors.dailyRate}
                </p>
              )}
            </div>
            <div>
              <label className={labelClass}>
                Joining date <span className="text-red-500">*</span>
              </label>
              <input
                className={inputClass}
                type="date"
                value={form.joinDate}
                onChange={(e) => handleChange("joinDate", e.target.value)}
              />
              {errors.joinDate && (
                <p className="text-xs text-red-500 mt-1.5">{errors.joinDate}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <div className="flex gap-2">
                {(["active", "inactive"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleChange("status", s)}
                    className={`flex-1 rounded-lg border px-3.5 py-2.5 text-sm font-medium capitalize transition-colors cursor-pointer ${form.status === s ? (s === "active" ? "border-teal-200 bg-teal-50 text-teal-700" : "border-gray-300 bg-gray-100 text-gray-700") : "border-gray-200 bg-white text-gray-400 hover:bg-gray-50"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          icon={<Clock size={17} />}
          title="Check-in / Check-out Buffer"
          description="Give the worker a grace period for both check-in and check-out."
        >
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-indigo-500" />
                <span className="text-sm font-medium text-slate-700">
                  Buffer timings
                </span>
              </div>
              <button
                type="button"
                onClick={() => setEnableTimings(!enableTimings)}
                className={`flex items-center gap-2 text-xs font-medium transition-colors cursor-pointer ${enableTimings ? "text-emerald-600" : "text-slate-400"}`}
              >
                {enableTimings ? (
                  <>
                    <ToggleRight className="h-6 w-6 text-emerald-500" />
                    <span>Enabled</span>
                  </>
                ) : (
                  <>
                    <ToggleLeft className="h-6 w-6 text-slate-300" />
                    <span>Disabled</span>
                  </>
                )}
              </button>
            </div>
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${!enableTimings ? "opacity-60 pointer-events-none" : ""}`}
            >
              <div>
                <label className={labelClass}>Check-in Buffer Time</label>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    className={inputClass}
                    value={form.checkInBufferTime}
                    onChange={(e) =>
                      handleChange("checkInBufferTime", e.target.value)
                    }
                    disabled={!enableTimings}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentTime("checkInBufferTime")}
                    disabled={!enableTimings}
                    className="shrink-0"
                  >
                    Now
                  </Button>
                </div>
                {errors.checkInBufferTime && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.checkInBufferTime}
                  </p>
                )}
              </div>
              <div>
                <label className={labelClass}>Check-out Buffer Time</label>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    className={inputClass}
                    value={form.checkOutBufferTime}
                    onChange={(e) =>
                      handleChange("checkOutBufferTime", e.target.value)
                    }
                    disabled={!enableTimings}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentTime("checkOutBufferTime")}
                    disabled={!enableTimings}
                    className="shrink-0"
                  >
                    Now
                  </Button>
                </div>
                {errors.checkOutBufferTime && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.checkOutBufferTime}
                  </p>
                )}
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          icon={<Phone size={17} />}
          title="Emergency contact"
          description="Who to reach in case of an incident"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5">
            <div>
              <label className={labelClass}>Contact person</label>
              <input
                className={inputClass}
                placeholder="e.g. Sunita Devi"
                value={form.contactPerson}
                onChange={(e) => handleChange("contactPerson", e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Contact number</label>
              <input
                className={inputClass}
                placeholder="10-digit mobile number"
                value={form.cpNumber}
                onChange={(e) => handleChange("cpNumber", e.target.value)}
              />
              {errors.cpNumber && (
                <p className="text-xs text-red-500 mt-1.5">{errors.cpNumber}</p>
              )}
            </div>
          </div>
        </FormSection>

        <div className="flex items-center justify-end gap-3 px-6 sm:px-8 py-4 bg-gray-50/70 border-t border-gray-100">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(RoutesLink.labor)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" className="gap-2" disabled={isSubmitting}>
            <Save size={16} />
            {isSubmitting
              ? "Saving…"
              : isEditMode
                ? "Update worker"
                : "Save worker"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateLabor;
